import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import * as Sentry from '@sentry/react';

import { isSedeMode } from '../../../../../utils/partnerAccess/partnerStorage';
import { getHumanTasksService } from '../human-tasks-service';
import { mapMyTasks } from '../mappers/my-tasks';
import { subscribeHumanTaskEvents } from '../../realtime/human-tasks-events';
import { isReportableReadError } from '../../helpers/errors';
import { humanTasksKeys } from './query-keys';

/**
 * `GET /human-tasks/me` doubles as the presence heartbeat (spec §4: only
 * humans with the panel open in the last 3 minutes receive tasks), so it is
 * polled while any screen of the dashboard is open for a task receiver; the
 * nav badge keeps it alive outside Inicio.
 *
 * In a sede tab it is polled only while the tab is visible (and on coming
 * back to it): the backend then drops her presence in her other linked sedes,
 * so she receives tasks only from the club she has open
 * (docs/specs/2026-11-04-inicio-en-multisede, S2).
 */
export const MY_TASKS_POLL_MS = 60000;

export function useMyTasks({ enabled = true } = {}) {
  return useQuery(humanTasksKeys.me(), () => getHumanTasksService().getMyTasks().then(mapMyTasks), {
    enabled,
    refetchInterval: enabled ? MY_TASKS_POLL_MS : false,
    refetchIntervalInBackground: !isSedeMode(),
    refetchOnWindowFocus: true,
    retry: 1,
    onError: (error) => {
      if (isReportableReadError(error)) {
        Sentry.captureException(error, { tags: { component: 'humanTasks', op: 'me' } });
      }
    },
  });
}

/** Every Pusher task event refetches the tasks and the stats of Inicio. */
export function useHumanTaskEventsRefresh() {
  const queryClient = useQueryClient();
  useEffect(
    () => subscribeHumanTaskEvents(() => {
      queryClient.invalidateQueries(humanTasksKeys.all);
    }),
    [queryClient],
  );
}

export default useMyTasks;
